import { authorizedAxiosInstance } from '@/clients';
import type { MetricChartCompany } from '@/shared/components/data-visualization/recharts';

export interface FinancialCompanySearchResult {
    companyName?: string;
    exchange?: string;
    exchangeShortName?: string;
    image?: string;
    marketCap?: number;
    name?: string;
    sector?: string;
    stockExchange?: string;
    symbol: string;
    website?: string;
}

export const DEFAULT_FINANCIAL_COMPANIES: MetricChartCompany[] = [
    { companyName: 'Apple', exchange: 'NASDAQ', symbol: 'AAPL' },
    { companyName: 'Microsoft', exchange: 'NASDAQ', symbol: 'MSFT' },
    { companyName: 'NVIDIA', exchange: 'NASDAQ', symbol: 'NVDA' },
    { companyName: 'Amazon', exchange: 'NASDAQ', symbol: 'AMZN' },
    { companyName: 'Alphabet', exchange: 'NASDAQ', symbol: 'GOOGL' },
    { companyName: 'Meta Platforms', exchange: 'NASDAQ', symbol: 'META' },
    { companyName: 'Berkshire Hathaway', exchange: 'NYSE', symbol: 'BRK.B' },
    { companyName: 'Tesla', exchange: 'NASDAQ', symbol: 'TSLA' },
    { companyName: 'JPMorgan Chase', exchange: 'NYSE', symbol: 'JPM' },
    { companyName: 'Visa', exchange: 'NYSE', symbol: 'V' },
];

export const financialCompanySearchResultToCompany = (
    result: FinancialCompanySearchResult,
): MetricChartCompany | undefined => {
    if (!result.symbol) {
        return undefined;
    }

    return {
        companyName: result.name ?? result.companyName ?? result.symbol,
        exchange: result.exchangeShortName ?? result.exchange ?? result.stockExchange,
        image: result.image,
        marketCap: result.marketCap,
        sector: result.sector,
        symbol: result.symbol,
        website: result.website,
    };
};

export const mergeEnrichedFinancialCompanies = (
    companies: MetricChartCompany[],
    profiles: FinancialCompanySearchResult[],
) => {
    const profilesBySymbol = new Map(
        profiles
            .map(financialCompanySearchResultToCompany)
            .filter((profile): profile is MetricChartCompany => Boolean(profile))
            .map((profile) => [profile.symbol, profile]),
    );

    return companies.map((company) => {
        const profile = profilesBySymbol.get(company.symbol);

        return profile
            ? {
                  ...company,
                  ...profile,
                  exchange: profile.exchange ?? company.exchange,
              }
            : company;
    });
};

export const enrichFinancialCompanies = async (companies: MetricChartCompany[], signal?: AbortSignal) => {
    if (!companies.length) {
        return [];
    }

    const response = await authorizedAxiosInstance.post<FinancialCompanySearchResult[]>(
        '/api/vendors/fmp/profiles/enriched',
        { symbols: companies.map(({ symbol }) => symbol) },
        { signal },
    );

    return mergeEnrichedFinancialCompanies(companies, response.data);
};

export const financialCompanyLabel = (company: MetricChartCompany) =>
    company.exchange
        ? `${company.companyName} (${company.symbol}, ${company.exchange})`
        : `${company.companyName} (${company.symbol})`;

export const searchFinancialCompanies = async (query: string, signal?: AbortSignal) => {
    const response = await authorizedAxiosInstance.get<FinancialCompanySearchResult[]>(
        '/api/vendors/fmp/search/general',
        {
            params: { limit: 12, query },
            signal,
        },
    );

    return response.data
        .map(financialCompanySearchResultToCompany)
        .filter((company): company is MetricChartCompany => Boolean(company));
};

export const searchEnrichedFinancialCompanies = async (query: string, signal?: AbortSignal) =>
    enrichFinancialCompanies(await searchFinancialCompanies(query, signal), signal);
